import { scale } from "@/utils/appStyleSheet";

// Font families
export const FontFamilies = {
  InterTight: {
    Thin: "InterTight-Thin",
    ExtraLight: "InterTight-ExtraLight",
    Light: "InterTight-Light",
    Regular: "InterTight-Regular",
    Medium: "InterTight-Medium",
    SemiBold: "InterTight-SemiBold",
    Bold: "InterTight-Bold",
    ExtraBold: "InterTight-ExtraBold",
  },
  Inter: {
    Thin: "Inter-Thin",
    ExtraLight: "Inter-ExtraLight",
    Light: "Inter-Light",
    Regular: "Inter-Regular",
    Medium: "Inter-Medium",
    SemiBold: "Inter-SemiBold",
    Bold: "Inter-Bold",
    ExtraBold: "Inter-ExtraBold",
  },
  SpaceMono: {
    Regular: "SpaceMono-Regular",
  },
} as const;

// Font sizes
export const FontSizes = {
  xs: scale(10),
  sm: scale(12),
  md: scale(14),
  lg: scale(16),
  xl: scale(18),
  "2xl": scale(20),
  "3xl": scale(24),
  "4xl": scale(28),
  "5xl": scale(32),
  "6xl": scale(36),
  "7xl": scale(48),
  "8xl": scale(64),
} as const;

// Font weights
export const FontWeights = {
  thin: "100",
  extraLight: "200",
  light: "300",
  regular: "400",
  medium: "500",
  semiBold: "600",
  bold: "700",
  extraBold: "800",
} as const;

// Line heights
export const LineHeights = {
  tight: scale(1.1),
  normal: scale(1.3),
  relaxed: scale(1.5),
  loose: scale(1.7),
} as const;

// Letter spacing
export const LetterSpacing = {
  tight: scale(-0.5),
  normal: 0,
  wide: scale(0.5),
  wider: scale(1),
} as const;

// Typography styles
export const Typography = {
  // Display styles
  display: {
    large: {
      fontFamily: FontFamilies.InterTight.ExtraBold,
      fontSize: FontSizes["7xl"],
      lineHeight: FontSizes["7xl"] * LineHeights.tight,
      letterSpacing: LetterSpacing.tight,
    },
    medium: {
      fontFamily: FontFamilies.InterTight.Bold,
      fontSize: FontSizes["6xl"],
      lineHeight: FontSizes["6xl"] * LineHeights.tight,
      letterSpacing: LetterSpacing.tight,
    },
    small: {
      fontFamily: FontFamilies.InterTight.Bold,
      fontSize: FontSizes["5xl"],
      lineHeight: FontSizes["5xl"] * LineHeights.tight,
      letterSpacing: LetterSpacing.normal,
    },
  },

  // Heading styles
  heading: {
    h1: {
      fontFamily: FontFamilies.InterTight.Bold,
      fontSize: FontSizes["4xl"],
      lineHeight: FontSizes["4xl"] * LineHeights.tight,
      letterSpacing: LetterSpacing.normal,
    },
    h2: {
      fontFamily: FontFamilies.InterTight.Bold,
      fontSize: FontSizes["3xl"],
      lineHeight: FontSizes["3xl"] * LineHeights.tight,
      letterSpacing: LetterSpacing.normal,
    },
    h3: {
      fontFamily: FontFamilies.InterTight.SemiBold,
      fontSize: FontSizes["2xl"],
      lineHeight: FontSizes["2xl"] * LineHeights.normal,
      letterSpacing: LetterSpacing.normal,
    },
    h4: {
      fontFamily: FontFamilies.InterTight.SemiBold,
      fontSize: FontSizes.xl,
      lineHeight: FontSizes.xl * LineHeights.normal,
      letterSpacing: LetterSpacing.normal,
    },
    h5: {
      fontFamily: FontFamilies.InterTight.Medium,
      fontSize: FontSizes.lg,
      lineHeight: FontSizes.lg * LineHeights.normal,
      letterSpacing: LetterSpacing.normal,
    },
    h6: {
      fontFamily: FontFamilies.InterTight.Medium,
      fontSize: FontSizes.md,
      lineHeight: FontSizes.md * LineHeights.normal,
      letterSpacing: LetterSpacing.normal,
    },
  },

  // Body text styles
  body: {
    large: {
      fontFamily: FontFamilies.InterTight.Regular,
      fontSize: FontSizes.lg,
      lineHeight: FontSizes.lg * LineHeights.relaxed,
      letterSpacing: LetterSpacing.normal,
    },
    medium: {
      fontFamily: FontFamilies.InterTight.Regular,
      fontSize: FontSizes.md,
      lineHeight: FontSizes.md * LineHeights.relaxed,
      letterSpacing: LetterSpacing.normal,
    },
    small: {
      fontFamily: FontFamilies.InterTight.Regular,
      fontSize: FontSizes.sm,
      lineHeight: FontSizes.sm * LineHeights.relaxed,
      letterSpacing: LetterSpacing.normal,
    },
  },

  // Label styles
  label: {
    large: {
      fontFamily: FontFamilies.InterTight.Medium,
      fontSize: FontSizes.md,
      lineHeight: FontSizes.md * LineHeights.normal,
      letterSpacing: LetterSpacing.normal,
    },
    medium: {
      fontFamily: FontFamilies.InterTight.Medium,
      fontSize: FontSizes.sm,
      lineHeight: FontSizes.sm * LineHeights.normal,
      letterSpacing: LetterSpacing.normal,
    },
    small: {
      fontFamily: FontFamilies.InterTight.Medium,
      fontSize: FontSizes.xs,
      lineHeight: FontSizes.xs * LineHeights.normal,
      letterSpacing: LetterSpacing.wide,
    },
  },

  // Button styles
  button: {
    large: {
      fontFamily: FontFamilies.InterTight.SemiBold,
      fontSize: FontSizes.lg,
      lineHeight: FontSizes.lg * LineHeights.normal,
      letterSpacing: LetterSpacing.normal,
    },
    medium: {
      fontFamily: FontFamilies.InterTight.SemiBold,
      fontSize: FontSizes.md,
      lineHeight: FontSizes.md * LineHeights.normal,
      letterSpacing: LetterSpacing.normal,
    },
    small: {
      fontFamily: FontFamilies.InterTight.SemiBold,
      fontSize: FontSizes.sm,
      lineHeight: FontSizes.sm * LineHeights.normal,
      letterSpacing: LetterSpacing.wide,
    },
  },

  // Caption styles
  caption: {
    large: {
      fontFamily: FontFamilies.InterTight.Regular,
      fontSize: FontSizes.sm,
      lineHeight: FontSizes.sm * LineHeights.normal,
      letterSpacing: LetterSpacing.normal,
    },
    medium: {
      fontFamily: FontFamilies.InterTight.Regular,
      fontSize: FontSizes.xs,
      lineHeight: FontSizes.xs * LineHeights.normal,
      letterSpacing: LetterSpacing.normal,
    },
  },

  // Overline styles
  overline: {
    fontFamily: FontFamilies.InterTight.SemiBold,
    fontSize: FontSizes.xs,
    lineHeight: FontSizes.xs * LineHeights.normal,
    letterSpacing: LetterSpacing.wider,
    textTransform: "uppercase" as const,
  },

  // Monospace styles
  monospace: {
    fontFamily: FontFamilies.SpaceMono.Regular,
    fontSize: FontSizes.sm,
    lineHeight: FontSizes.sm * LineHeights.relaxed,
    letterSpacing: LetterSpacing.normal,
  },
} as const;

// Type exports for TypeScript
export type FontFamily = keyof typeof FontFamilies;
export type FontSize = keyof typeof FontSizes;
export type FontWeight = keyof typeof FontWeights;
export type LineHeight = keyof typeof LineHeights;
export type LetterSpacing = keyof typeof LetterSpacing;
export type TypographyStyle = typeof Typography;
